/*
 * Copyright 2025 GnosisPay
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type {
  GPSDKInitConfig,
  GPSDKInitConfigV3,
  GPSDKConfig,
  ElementOptions,
  ElementOptionsV1,
  ElementOptionsV2,
  Environment,
  ElementStyle,
  SDKMessage,
  IframeMessage,
  ActionSuccessCallback,
  EphemeralTokenInvalidCallback,
  ErrorCallback,
} from "./types";

import { ElementType, IframeMessageType, Action } from "./types";
import pkg from "../package.json" assert { type: "json" };

export { ElementType, IframeMessageType, Action };

export type {
  GPSDKInitConfig,
  GPSDKInitConfigV3,
  GPSDKConfig,
  ElementOptions,
  ElementOptionsV1,
  ElementOptionsV2,
  Environment,
  ElementStyle,
  SDKMessage,
  IframeMessage,
  ActionSuccessCallback,
  EphemeralTokenInvalidCallback,
  ErrorCallback,
};

const messageActionSuccessMap: Partial<Record<IframeMessageType, Action>> = {
  [IframeMessageType.CardNumberCopied]: Action.CardNumberCopied,
  [IframeMessageType.CardExpirationCopied]: Action.CardExpirationCopied,
  [IframeMessageType.CardSecurityCodeCopied]: Action.CardSecurityCodeCopied,
  [IframeMessageType.DoneSettingPin]: Action.DoneSettingPin,
  [IframeMessageType.SetPinSuccess]: Action.SetPin,
};

class GPSDK {
  private config: GPSDKConfig;
  private iframeSrc?: string;
  private callbacks: {
    onActionSuccess?: ActionSuccessCallback;
    onInvalidToken?: EphemeralTokenInvalidCallback;
    onError?: ErrorCallback;
  };
  private static defaultConfig = {
    environment: "production" as const,
    iframeHost: "https://api-pse-public.gnosispay.com",
  };

  constructor(config: GPSDKInitConfig) {
    if (!config.ephemeralToken) {
      throw new Error(
        "ephemeralToken is required and SDK cannot be initialized without it"
      );
    }

    this.config = {
      ...GPSDK.defaultConfig,
      pseVersion: 1,
      ...config,
    };

    this.callbacks = {
      onActionSuccess: config.onActionSuccess,
      onInvalidToken: config.onInvalidToken,
      onError: config.onError,
    };

    window.addEventListener("message", this.handleMessage.bind(this));
  }

  /**
   * Handle messages from the iframe
   * @param event The message event
   */
  private handleMessage(event: MessageEvent) {
    // Verify the message origin
    if (event.origin !== this.config.iframeHost) {
      return;
    }

    // Verify the message is from our iframe
    const iframe = document.querySelector(
      `iframe[src="${this.iframeSrc}"]`
    ) as HTMLIFrameElement | null;

    if (!iframe || event.source !== iframe.contentWindow) {
      return;
    }

    // Process the message
    const message = event.data as IframeMessage;
    const actionSuccess = messageActionSuccessMap[message.type];

    if (actionSuccess && this.callbacks.onActionSuccess && message?.type) {
      this.callbacks.onActionSuccess(actionSuccess);
    }

    if (
      message?.type === IframeMessageType.EphemeralTokenInvalid &&
      this.callbacks.onInvalidToken
    ) {
      this.callbacks.onInvalidToken(message.message);
    }

    if (message?.type === IframeMessageType.Error && this.callbacks.onError) {
      this.callbacks.onError(message.message, message.details);
    }
  }

  /**
   * Initialize and mount a PCI-compliant element
   * @param elementType The type of element to create
   * @param selector The CSS selector where the element should be mounted
   * @param options Element options (v1: { cardToken, style }, v2: { cardId, style })
   */
  public init(
    elementType: ElementType,
    selector: string,
    options: ElementOptions
  ) {
    const validElementTypes = Object.values(ElementType);
    if (!validElementTypes.includes(elementType)) {
      throw new Error(
        `Invalid elementType '${elementType}'. Must be one of: ${validElementTypes.join(
          ", "
        )}`
      );
    }

    const container = document.querySelector(selector);
    if (!container) {
      throw new Error(`Element with selector '${selector}' not found`);
    }

    const iframe = document.createElement("iframe");
    this.iframeSrc = `${this.config.iframeHost}/?appId=${this.config.appId}`;
    iframe.src = this.iframeSrc;
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.allow = "clipboard-write";

    container.innerHTML = "";
    container.appendChild(iframe);

    // Send initialization data once iframe is loaded
    iframe.onload = () => {
      if (!iframe.contentWindow) {
        throw new Error("Iframe content window is not accessible");
      }

      const targetOrigin = this.config.iframeHost;

      if (this.config.pseVersion === 3) {
        // V3 flow — EIP-712 2FA
        const v2Options = options as ElementOptionsV2;
        iframe.contentWindow.postMessage(
          {
            type: "init",
            authModuleToken: this.config.authModuleToken,
            eip712Signature: this.config.eip712Signature,
            eip712Nonce: this.config.eip712Nonce,
            cardId: v2Options.cardId,
            ephemeralToken: this.config.ephemeralToken,
            elementType,
            appId: this.config.appId,
            version: pkg.version,
            style: v2Options.style,
          } satisfies SDKMessage,
          targetOrigin
        );
      } else {
        // V1 flow
        const v1Options = options as ElementOptionsV1;
        iframe.contentWindow.postMessage(
          {
            type: "init",
            cardToken: v1Options.cardToken,
            ephemeralToken: this.config.ephemeralToken,
            gnosisPayApiAuthToken: this.config.gnosisPayApiAuthToken,
            style: v1Options.style,
            elementType,
            appId: this.config.appId,
            version: pkg.version,
          } satisfies SDKMessage,
          targetOrigin
        );
      }
    };

    return {
      destroy: () => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      },
    };
  }

  /**
   * Refresh the ephemeral token and notify the iframe
   * @param newToken The new ephemeral token to use
   */
  public refreshToken(newToken: string) {
    // Validate the new token
    if (!newToken) {
      throw new Error(
        "ephemeralToken is required and cannot be null or undefined"
      );
    }

    // Update the token in the config
    this.config.ephemeralToken = newToken;

    // Find the iframe and send the refresh message
    const iframe = document.querySelector(
      `iframe[src="${this.iframeSrc}"]`
    ) as HTMLIFrameElement | null;

    if (!iframe || !iframe.contentWindow) {
      throw new Error("Iframe not found or not accessible");
    }

    const targetOrigin = this.config.iframeHost;

    iframe.contentWindow.postMessage(
      {
        type: "tokenRefresh",
        ephemeralToken: newToken,
      } satisfies SDKMessage,
      targetOrigin
    );
  }
}

export default GPSDK;
